import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";

const serviceOptions = [
  "Tree Trimming / Pruning",
  "Tree Removal",
  "Stump Grinding",
  "Emergency Tree Service",
  "Not Sure / Multiple Services",
];

const trustBadges = [
  { icon: "ri-shield-check-line", label: "Licensed & Insured Pros" },
  { icon: "ri-time-line", label: "Response Within 24 Hours" },
  { icon: "ri-price-tag-3-line", label: "No Obligation, Ever" },
  { icon: "ri-lock-line", label: "Your Info Stays Private" },
];

const steps = [
  { num: "1", title: "Fill Out the Form", desc: "Takes less than 60 seconds. Just your name, phone, zip, and service type." },
  { num: "2", title: "Get Matched Locally", desc: "We connect you with licensed, insured tree pros in your specific zip code." },
  { num: "3", title: "Receive Free Quotes", desc: "Compare estimates from multiple pros — no pressure, no obligation to hire." },
];

export default function FreeQuotePage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => data.append(key, value as string));
    try {
      await fetch("https://readdy.ai/api/form/d7938lhrcamcd36dbb70", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SiteNavbar />

      {/* Page Header */}
      <section className="bg-[#1B4332] pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block bg-green-400/20 border border-green-400/30 text-green-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            100% Free · No Obligation
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 leading-tight">
            Get Your Free Tree Service Quote
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Tell us about your project and we&apos;ll connect you with licensed local pros who will give you a free, no-pressure estimate.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 bg-[#F1F8F4]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step) => (
              <div key={step.num} className="flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1B4332] text-white font-extrabold text-xl rounded-full">
                  {step.num}
                </div>
                <h3 className="font-bold text-[#1B4332] text-base">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#F8FAF9] rounded-2xl p-8 md:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-20 h-20 flex items-center justify-center bg-[#E8F5E9] rounded-full mb-6">
                      <i className="ri-checkbox-circle-line text-[#1B4332] text-4xl"></i>
                    </div>
                    <h2 className="text-2xl font-extrabold text-[#1B4332] mb-3">Request Received!</h2>
                    <p className="text-gray-500 text-base max-w-sm mb-6">
                      A licensed local tree pro will reach out within 24 hours with your free, no-obligation quote.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setSubmitted(false)}
                        className="px-6 py-2.5 border border-[#1B4332] text-[#1B4332] font-semibold text-sm rounded-full cursor-pointer whitespace-nowrap hover:bg-[#E8F5E9] transition-colors"
                      >
                        Submit Another
                      </button>
                      <button
                        onClick={() => navigate("/cost-guide")}
                        className="px-6 py-2.5 bg-[#1B4332] text-white font-semibold text-sm rounded-full cursor-pointer whitespace-nowrap hover:bg-[#2d6a4f] transition-colors"
                      >
                        View Pricing Guide
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-extrabold text-[#1B4332] mb-1">Request Your Free Quote</h2>
                    <p className="text-gray-500 text-sm mb-7">All fields marked * are required.</p>
                    <form
                      data-readdy-form
                      id="free-quote-page-form"
                      onSubmit={handleSubmit}
                      className="flex flex-col gap-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="fq-name" className="text-sm font-semibold text-[#1B4332]">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="fq-name"
                            name="name"
                            type="text"
                            required
                            placeholder="Jane Smith"
                            className="h-12 px-4 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="fq-phone" className="text-sm font-semibold text-[#1B4332]">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="fq-phone"
                            name="phone"
                            type="tel"
                            required
                            placeholder="(555) 000-0000"
                            className="h-12 px-4 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="fq-zip" className="text-sm font-semibold text-[#1B4332]">
                            Zip Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="fq-zip"
                            name="zip"
                            type="text"
                            required
                            placeholder="e.g. 90210"
                            maxLength={10}
                            className="h-12 px-4 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="fq-service" className="text-sm font-semibold text-[#1B4332]">
                            Service Type <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="fq-service"
                              name="service"
                              required
                              defaultValue=""
                              className="h-12 w-full px-4 pr-10 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors appearance-none cursor-pointer"
                            >
                              <option value="" disabled>Select a service...</option>
                              {serviceOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                              <i className="ri-arrow-down-s-line"></i>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="fq-email" className="text-sm font-semibold text-[#1B4332]">
                          Email Address <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                          id="fq-email"
                          name="email"
                          type="email"
                          placeholder="jane@example.com"
                          className="h-12 px-4 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors"
                        />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="fq-comments" className="text-sm font-semibold text-[#1B4332]">
                          Additional Details <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                          id="fq-comments"
                          name="comments"
                          rows={4}
                          maxLength={500}
                          placeholder="Describe your trees — size, number, location on property, any urgency..."
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#1B4332] transition-colors resize-none"
                        />
                        <span className="text-xs text-gray-400 text-right">{charCount}/500</span>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-14 bg-[#1B4332] hover:bg-[#2d6a4f] text-white font-bold text-base rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {loading ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
                        ) : (
                          <>Get My Free Quote <i className="ri-arrow-right-line"></i></>
                        )}
                      </button>

                      <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
                        <i className="ri-lock-line"></i>
                        Your information is secure and never sold to third parties.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Trust Badges */}
              <div className="bg-[#1B4332] rounded-2xl p-6">
                <h3 className="text-white font-bold text-base mb-5">Why Homeowners Trust Us</h3>
                <div className="flex flex-col gap-4">
                  {trustBadges.map((b) => (
                    <div key={b.label} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-white/15 rounded-lg flex-shrink-0">
                        <i className={`${b.icon} text-green-300 text-base`}></i>
                      </div>
                      <span className="text-white/85 text-sm font-medium">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Reminder */}
              <div className="bg-[#F1F8F4] rounded-2xl p-6">
                <h3 className="text-[#1B4332] font-bold text-base mb-4">Typical Price Ranges</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { label: "Small tree trim", price: "$150–$400" },
                    { label: "Medium tree trim", price: "$400–$900" },
                    { label: "Large tree trim", price: "$900–$1,800" },
                    { label: "Stump grinding", price: "$100–$400" },
                    { label: "Emergency removal", price: "$500–$2,500" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between">
                      <span className="text-gray-600 text-sm">{item.label}</span>
                      <span className="text-[#1B4332] font-bold text-sm">{item.price}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate("/cost-guide")}
                  className="mt-5 w-full py-2.5 border border-[#1B4332] text-[#1B4332] font-semibold text-sm rounded-lg hover:bg-[#E8F5E9] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Full Pricing Guide <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>

              {/* Testimonial */}
              <div className="bg-white border border-gray-100 rounded-2xl p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed italic mb-4">
                  &ldquo;Got three quotes within a day. Saved over $400 compared to the first company I called. Absolutely worth it!&rdquo;
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <i className="ri-user-line text-[#1B4332] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#1B4332]">Sarah M.</p>
                    <p className="text-xs text-gray-400">Austin, TX</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
